// third-party
import { createSlice } from '@reduxjs/toolkit';

// project-imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// types
import { chat } from './chat';
import { showSuccessToast } from 'utils/showSuccessToast';
import {
  AttendanceSettingsStateProps,
  AutoAttendanceSettings,
  BusinessHoursSettings,
  FinishReasonPayload
} from 'types/attendanceSettings';

export const urlSettings = process.env.REACT_APP_API_URL + '/api/settings/v1/attendance';
export const urlReasons = process.env.REACT_APP_API_URL + '/api/attendance/v1/finish-reasons';

const SAVED_MESSAGE = 'Configurações salvas. Valem para as próximas conversas.';

const initialState: AttendanceSettingsStateProps = {
  settings: null,
  finishReasons: []
};

export const attendanceSettings = createSlice({
  name: 'attendanceSettings',
  initialState,
  reducers: {
    setSettings(state, action) {
      state.settings = action.payload;
    },
    setFinishReasons(state, action) {
      state.finishReasons = action.payload;
    }
  }
});

// Os thunks de escrita relançam o erro: o interceptor do axios já exibe o toast (400/403/409),
// e a tela usa o rethrow pra não fechar diálogo nem limpar formulário quando falha.

export function getAttendanceSettings() {
  return async () => {
    try {
      const response = await axios.get(urlSettings);
      dispatch(attendanceSettings.actions.setSettings(response.data));
    } catch (error) {
      dispatch(chat.actions.hasError(error));
    }
  };
}

async function putSettings(patch: { autoAttendance?: AutoAttendanceSettings; businessHours?: BusinessHoursSettings }) {
  try {
    const response = await axios.put(urlSettings, patch);
    dispatch(attendanceSettings.actions.setSettings(response.data));
    showSuccessToast(SAVED_MESSAGE);
  } catch (error) {
    dispatch(chat.actions.hasError(error));
    throw error;
  }
}

export function saveAutoAttendance(autoAttendance: AutoAttendanceSettings) {
  return async () => putSettings({ autoAttendance });
}

export function saveBusinessHours(businessHours: BusinessHoursSettings) {
  return async () => putSettings({ businessHours });
}

export function getFinishReasons() {
  return async () => {
    try {
      const response = await axios.get(urlReasons);
      dispatch(attendanceSettings.actions.setFinishReasons(response.data));
    } catch (error) {
      dispatch(chat.actions.hasError(error));
    }
  };
}

export function createFinishReason(payload: FinishReasonPayload) {
  return async () => {
    try {
      await axios.post(urlReasons, payload);
      await dispatch(getFinishReasons());
      showSuccessToast('Motivo criado com sucesso');
    } catch (error) {
      dispatch(chat.actions.hasError(error));
      throw error;
    }
  };
}

export function updateFinishReason(id: string, payload: FinishReasonPayload) {
  return async () => {
    try {
      await axios.put(`${urlReasons}/${id}`, payload);
      await dispatch(getFinishReasons());
      showSuccessToast('Motivo atualizado com sucesso');
    } catch (error) {
      dispatch(chat.actions.hasError(error));
      throw error;
    }
  };
}

export function reorderFinishReasons(ids: string[]) {
  return async () => {
    try {
      const response = await axios.put(`${urlReasons}/order`, { ids });
      dispatch(attendanceSettings.actions.setFinishReasons(response.data));
    } catch (error) {
      dispatch(chat.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteFinishReason(id: string) {
  return async () => {
    try {
      await axios.delete(`${urlReasons}/${id}`);
      await dispatch(getFinishReasons());
      showSuccessToast('Motivo excluído com sucesso');
    } catch (error) {
      dispatch(chat.actions.hasError(error));
      throw error;
    }
  };
}

export default attendanceSettings.reducer;
